import React, { useState } from 'react';
import {
  Shield, BookOpen, Zap, TrendingUp, Sparkles,
  Target, History, PieChart, Map,
  FlaskConical, Brain, ArrowRight, ChevronDown, ChevronUp, Lock,
} from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';

// ─── Journey stages ───────────────────────────────────────────────────────────
const STAGES = [
  {
    num: '01',
    label: 'Know Yourself',
    color: '#6c63ff',
    truth: 'Before you risk a rupee, understand who you are as a trader.',
    ifYou: 'You just signed up — or you\'ve been losing without knowing why.',
    badge: null,
    actions: [
      {
        icon: Shield,
        label: 'Complete Risk Profile',
        desc: 'Sets your capital tier and defines your risk tolerance. Everything else adjusts to this.',
        path: '/profile?section=risk-profile',
        time: '5 min · do once',
      },
      {
        icon: Map,
        label: 'Explore the App Guide',
        desc: 'New here? The App Guide walks you through every feature — tap any node to see how it works.',
        path: '/appguide',
        time: 'Do once',
      },
      {
        icon: BookOpen,
        label: 'Start Learning',
        desc: 'Level 1 covers the fundamentals every trader needs — Stop Loss, R:R, position sizing.',
        path: '/learning',
        time: '10 min/day',
      },
      {
        icon: Zap,
        label: 'First ACT Scenario',
        desc: 'Apply what you learned. Make a real sim decision before you make a real one.',
        path: '/act',
        time: '5 min',
      },
    ],
  },
  {
    num: '02',
    label: 'Build Your Routine',
    color: '#4ecca3',
    truth: 'Profitable trading is boring. Same 3 steps, every single day.',
    ifYou: 'You understand the basics but your results are inconsistent.',
    badge: null,
    actions: [
      {
        icon: TrendingUp,
        label: 'Check Global Cues',
        desc: 'SGX Nifty, US futures, FII/DII flows. 5 minutes. Sets your bias for the entire day.',
        path: '/global-cues',
        time: 'Every morning',
      },
      {
        icon: Sparkles,
        label: 'Run the Decision Engine',
        desc: 'Grade your session before placing any trade. If it\'s Sit Out, sit out.',
        path: '/appguide?tab=journey',
        time: 'Pre-trade',
      },
      {
        icon: Target,
        label: 'Place a Trade',
        desc: 'Every trade needs three things: a Stop Loss, a target, and a written reason.',
        path: '/trade',
        time: 'Market hours',
      },
      {
        icon: History,
        label: 'Evening Review',
        desc: 'Re-read the reason you wrote at entry. See what actually happened. Be honest.',
        path: '/trade-history',
        time: 'After 3:30 PM',
      },
      {
        icon: Shield,
        label: 'Check Your Discipline Score',
        desc: 'Find the one rule you broke today. Name it. Fix it tomorrow.',
        path: '/discipline',
        time: 'Daily',
      },
    ],
  },
  {
    num: '03',
    label: 'Find Your Edge',
    color: '#f97316',
    truth: 'After 50 trades, the data shows you what you\'re actually good at.',
    ifYou: 'You\'re trading regularly but don\'t know if your approach actually works.',
    badge: 'After 50+ trades',
    actions: [
      {
        icon: PieChart,
        label: 'Trading Analytics',
        desc: 'Win rate, expectancy, average win vs. loss. This tells you if your edge is real or imagined.',
        path: '/trading-analytics',
        time: 'Weekly',
        premium: true,
      },
      {
        icon: Shield,
        label: 'Discipline Patterns',
        desc: 'Which rules do you break most often? Your biggest leak is hiding here.',
        path: '/discipline',
        time: 'Weekly',
      },
      {
        icon: Map,
        label: 'Monthly Performance Report',
        desc: 'A complete picture of your trading month. Accountability in numbers.',
        path: '/profile',
        time: 'Monthly',
      },
    ],
  },
  {
    num: '04',
    label: 'Build a System',
    color: '#ce93d8',
    truth: 'Once you\'re consistently profitable manually, then — and only then — you automate.',
    ifYou: 'You have a repeatable edge and want to remove emotion from execution entirely.',
    badge: 'When consistently profitable',
    actions: [
      {
        icon: FlaskConical,
        label: 'Backtest Your Strategy',
        desc: 'Prove your strategy works on months of historical data before scaling it.',
        path: '/backtest',
        time: 'Before scaling',
        premium: true,
      },
      {
        icon: Brain,
        label: 'Build Algo Flows',
        desc: 'Code your entry and exit rules. Let the system execute. Remove yourself from the equation.',
        path: '/algo-flows',
        time: 'When ready',
        premium: true,
      },
    ],
  },
];

// ─── Single action row ────────────────────────────────────────────────────────
function ActionRow({ action, color, navigate, meetsMinPlan }) {
  const Icon = action.icon;
  const premiumLocked = action.premium && !meetsMinPlan('premium');
  const handleClick = () => {
    if (premiumLocked) {
      navigate('/profile?section=subscription');
    } else {
      navigate(action.path);
    }
  };
  return (
    <button
      onClick={handleClick}
      className="w-full flex items-start gap-3 text-left group"
      style={{ padding: '10px 0', borderBottom: 'none', opacity: premiumLocked ? 0.75 : 1 }}
    >
      {/* Icon */}
      <div
        className="flex-shrink-0 flex items-center justify-center rounded-xl relative"
        style={{
          width: 38,
          height: 38,
          background: premiumLocked ? 'rgba(234,179,8,0.1)' : `${color}18`,
          border: `1.5px solid ${premiumLocked ? 'rgba(234,179,8,0.3)' : color + '35'}`,
          marginTop: 1,
        }}
      >
        <Icon size={16} color={premiumLocked ? '#eab308' : color} />
        {premiumLocked && (
          <div style={{
            position: 'absolute', top: -3, right: -3,
            width: 12, height: 12, borderRadius: '50%',
            background: 'rgba(234,179,8,0.2)',
            border: '1px solid rgba(234,179,8,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Lock size={6} color="#eab308" />
          </div>
        )}
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-[13px] font-bold leading-tight" style={{ color: '#e8eaf0' }}>
            {action.label}
          </p>
          {premiumLocked ? (
            <span
              className="text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
              style={{ background: 'rgba(234,179,8,0.15)', color: '#eab308', border: '1px solid rgba(234,179,8,0.3)' }}
            >
              Premium
            </span>
          ) : (
            <span
              className="text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
              style={{ background: `${color}18`, color: color, border: `1px solid ${color}30` }}
            >
              {action.time}
            </span>
          )}
        </div>
        <p className="text-[11px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.42)' }}>
          {action.desc}
        </p>
      </div>

      {/* Arrow */}
      {premiumLocked
        ? <Lock size={13} className="flex-shrink-0 mt-2.5" color="#eab308" style={{ opacity: 0.7 }} />
        : <ArrowRight size={14} className="flex-shrink-0 mt-2.5 transition-transform group-hover:translate-x-0.5" color={color} style={{ opacity: 0.6 }} />}
    </button>
  );
}

// ─── Single stage card ────────────────────────────────────────────────────────
function StageCard({ stage, index, navigate, meetsMinPlan }) {
  const [open, setOpen] = useState(index <= 1); // Stages 1 and 2 open by default
  const isLast  = index === STAGES.length - 1;
  const isDimmed = index >= 2;

  return (
    <div style={{ opacity: isDimmed ? 0.82 : 1, transition: 'opacity 0.2s' }}>
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: open
            ? `linear-gradient(160deg, ${stage.color}0d 0%, #0d0e1c 100%)`
            : '#0d0e1c',
          border: `1px solid ${open ? stage.color + '35' : 'rgba(255,255,255,0.07)'}`,
          transition: 'border-color 0.25s, background 0.25s',
        }}
      >
        {/* ── Stage header (always visible) ── */}
        <button
          className="w-full text-left"
          onClick={() => setOpen(o => !o)}
          style={{ padding: '16px 16px 14px' }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">

              {/* Stage number + label row */}
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="text-[11px] font-black tracking-widest"
                  style={{ color: stage.color, opacity: 0.7 }}
                >
                  {stage.num}
                </span>
                <div
                  className="h-px flex-1"
                  style={{ background: `${stage.color}30`, maxWidth: 24 }}
                />
                <span
                  className="text-[11px] font-black uppercase tracking-widest"
                  style={{ color: stage.color }}
                >
                  {stage.label}
                </span>
                {stage.badge && (
                  <span
                    className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                    style={{
                      background: `${stage.color}15`,
                      color: stage.color,
                      border: `1px solid ${stage.color}30`,
                    }}
                  >
                    {stage.badge}
                  </span>
                )}
              </div>

              {/* Truth line */}
              <p
                className="text-[15px] font-black leading-snug"
                style={{ color: '#eceef5', letterSpacing: '-0.01em' }}
              >
                {stage.truth}
              </p>
            </div>

            {/* Chevron */}
            <div
              className="flex-shrink-0 flex items-center justify-center rounded-full mt-0.5"
              style={{
                width: 26,
                height: 26,
                background: `${stage.color}15`,
                border: `1px solid ${stage.color}30`,
              }}
            >
              {open
                ? <ChevronUp size={13} color={stage.color} />
                : <ChevronDown size={13} color={stage.color} />}
            </div>
          </div>
        </button>

        {/* ── Expanded content ── */}
        {open && (
          <div style={{ padding: '0 16px 16px' }}>

            {/* "You're here if..." */}
            <div
              className="rounded-xl px-3 py-2.5 mb-4 flex items-start gap-2"
              style={{
                background: `${stage.color}0e`,
                border: `1px solid ${stage.color}28`,
              }}
            >
              <div
                className="flex-shrink-0 rounded-full mt-0.5"
                style={{ width: 6, height: 6, background: stage.color, marginTop: 5 }}
              />
              <div>
                <p
                  className="text-[9px] font-black uppercase tracking-widest mb-0.5"
                  style={{ color: stage.color }}
                >
                  You're here if
                </p>
                <p className="text-[12px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  {stage.ifYou}
                </p>
              </div>
            </div>

            {/* Action rows */}
            <div
              className="rounded-xl overflow-hidden"
              style={{ border: `1px solid rgba(255,255,255,0.06)`, background: 'rgba(0,0,0,0.25)' }}
            >
              {stage.actions.map((action, i) => (
                <div
                  key={action.label}
                  style={{
                    padding: '0 12px',
                    borderBottom: i < stage.actions.length - 1
                      ? '1px solid rgba(255,255,255,0.05)'
                      : 'none',
                  }}
                >
                  <ActionRow
                    action={action}
                    color={stage.color}
                    navigate={navigate}
                    meetsMinPlan={meetsMinPlan}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Connector between stages */}
      {!isLast && (
        <div className="flex justify-center" style={{ height: 28, alignItems: 'center' }}>
          <div
            style={{
              width: 1.5,
              height: '100%',
              background: `linear-gradient(180deg, ${stage.color}40, ${STAGES[index + 1].color}40)`,
            }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────
export default function OnboardingMap({ colors, navigate }) {
  const { meetsMinPlan } = usePermissions();
  const [isFirstVisit] = React.useState(() => {
    const seen = localStorage.getItem('tr_journey_banner_dismissed');
    return !seen;
  });
  const [showWelcome, setShowWelcome] = React.useState(isFirstVisit);

  const dismissWelcome = () => {
    localStorage.setItem('tr_journey_banner_dismissed', '1');
    setShowWelcome(false);
  };

  return (
    <div className="flex flex-col gap-0">

      {/* ── First-visit welcome banner ── */}
      {showWelcome && (
        <div
          className="rounded-2xl p-4 mb-3 flex items-start gap-3"
          style={{
            background: 'linear-gradient(135deg, #6c63ff22 0%, #4ecca318 100%)',
            border: '1px solid rgba(108,99,255,0.35)',
          }}
        >
          <div
            className="flex-shrink-0 flex items-center justify-center rounded-xl"
            style={{ width: 40, height: 40, background: '#6c63ff20', border: '1px solid #6c63ff35' }}
          >
            <span style={{ fontSize: 20 }}>👋</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-black mb-0.5" style={{ color: '#eceef5' }}>
              Welcome to TradeReflex
            </p>
            <p className="text-[11px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Your risk profile is set. Below are the 4 stages every trader goes through — start at Stage 1 and work down.
            </p>
            <button
              onClick={dismissWelcome}
              className="mt-2 text-[11px] font-bold"
              style={{ color: '#6c63ff' }}
            >
              Got it →
            </button>
          </div>
        </div>
      )}

      {/* ── Hero ── */}
      <div
        className="rounded-2xl p-4 mb-4"
        style={{
          background: 'linear-gradient(135deg, #6c63ff1a 0%, #4ecca30d 50%, #0d0e1c 100%)',
          border: '1px solid rgba(108,99,255,0.25)',
        }}
      >
        <p
          className="text-[11px] font-black uppercase tracking-widest mb-2"
          style={{ color: '#6c63ff' }}
        >
          Your Journey
        </p>
        <p
          className="text-[18px] font-black leading-tight mb-2"
          style={{ color: '#eceef5', letterSpacing: '-0.02em' }}
        >
          One page. Everything you need to trade with discipline.
        </p>
        <p className="text-[12px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>
          4 stages. Every trader starts at Stage 1.
          Find where you are — and what to do next.
        </p>

        {/* Stage flow diagram — 2×2 grid */}
        <div className="mt-3" style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gridTemplateRows: 'auto auto auto', gap: '4px 0', alignItems: 'center' }}>
          {/* Row 1: Stage 1 → arrow → Stage 2 */}
          <div className="rounded-xl px-2.5 py-2 flex items-center gap-1.5" style={{ background: `${STAGES[0].color}18`, border: `1px solid ${STAGES[0].color}35` }}>
            <span className="text-[9px] font-black tracking-widest flex-shrink-0" style={{ color: STAGES[0].color, opacity: 0.7 }}>{STAGES[0].num}</span>
            <span className="text-[10px] font-black leading-tight" style={{ color: STAGES[0].color }}>{STAGES[0].label}</span>
          </div>
          <div className="flex items-center justify-center px-1.5">
            <ArrowRight size={10} color="rgba(255,255,255,0.2)" />
          </div>
          <div className="rounded-xl px-2.5 py-2 flex items-center gap-1.5" style={{ background: `${STAGES[1].color}18`, border: `1px solid ${STAGES[1].color}35` }}>
            <span className="text-[9px] font-black tracking-widest flex-shrink-0" style={{ color: STAGES[1].color, opacity: 0.7 }}>{STAGES[1].num}</span>
            <span className="text-[10px] font-black leading-tight" style={{ color: STAGES[1].color }}>{STAGES[1].label}</span>
          </div>

          {/* Row 2: down arrow on left, empty, down arrow on right */}
          <div className="flex justify-center py-0.5">
            <div style={{ width: 1.5, height: 12, background: `linear-gradient(180deg, ${STAGES[0].color}40, ${STAGES[2].color}40)` }} />
          </div>
          <div />
          <div className="flex justify-center py-0.5">
            <div style={{ width: 1.5, height: 12, background: `linear-gradient(180deg, ${STAGES[1].color}40, ${STAGES[3].color}40)` }} />
          </div>

          {/* Row 3: Stage 3 → arrow → Stage 4 */}
          <div className="rounded-xl px-2.5 py-2 flex items-center gap-1.5" style={{ background: `${STAGES[2].color}18`, border: `1px solid ${STAGES[2].color}35` }}>
            <span className="text-[9px] font-black tracking-widest flex-shrink-0" style={{ color: STAGES[2].color, opacity: 0.7 }}>{STAGES[2].num}</span>
            <span className="text-[10px] font-black leading-tight" style={{ color: STAGES[2].color }}>{STAGES[2].label}</span>
          </div>
          <div className="flex items-center justify-center px-1.5">
            <ArrowRight size={10} color="rgba(255,255,255,0.2)" />
          </div>
          <div className="rounded-xl px-2.5 py-2 flex items-center gap-1.5" style={{ background: `${STAGES[3].color}18`, border: `1px solid ${STAGES[3].color}35` }}>
            <span className="text-[9px] font-black tracking-widest flex-shrink-0" style={{ color: STAGES[3].color, opacity: 0.7 }}>{STAGES[3].num}</span>
            <span className="text-[10px] font-black leading-tight" style={{ color: STAGES[3].color }}>{STAGES[3].label}</span>
          </div>
        </div>
      </div>

      {/* ── Stages ── */}
      {STAGES.map((stage, i) => (
        <StageCard
          key={stage.num}
          stage={stage}
          index={i}
          navigate={navigate}
          meetsMinPlan={meetsMinPlan}
        />
      ))}

    </div>
  );
}
